// 03-11 (rebrand; Pau: «si paso un chat a contrato con Santi que se me agrupe en otro lugar»): lo que la lista y la
// oficina necesitan saber de los contratos con Santi de cada agente, calculado de la lista de contratos (la misma que
// pinta su tablero, viva por el socket `oficina:pelotas`). Puro + un hook que asegura que la lista está cargada.
//
// 04-11 (mesas de equipo, grupos.md regla 9): un grupo de mesas se puede ATAR a un contrato con Santi. Mientras la pelota
// de ese contrato la tiene Santi, los agentes del grupo cuentan como «con Santi» aunque no tengan contrato propio. El
// estado lo dicta SIEMPRE el contrato (su `lado`), nunca una casilla: el grupo solo guarda QUÉ contrato. Un contrato
// cerrado ya no está en la lista de abiertos, así que el grupo deja de esperar solo. Este es el ÚNICO lector que se
// amplía (auditoría de la fuente única): la lista, la escena y aparcar ya leen `conSanti` de aquí.
import { useEffect, useMemo } from 'react';
import type { ContratadoDTO, GrupoDTO } from '../compartido/vocabulario';
import type { SantiDelAgente, SantiPorAgente } from '../marco/situacion';
import { useOficinaStore } from '../store';
import { usePelotasStore } from './store';
import { YO } from './tiempo';
import type { ContratoDTO } from './tipos';

/** Lo que espera un grupo atado: el contrato (abierto) y si la pelota la tiene Santi. */
export interface EsperaDeGrupo {
  contrato: ContratoDTO;
  conSanti: boolean;
}

/** Por grupo atado a un contrato ABIERTO: el contrato y de qué lado está la pelota. Los atados a uno cerrado no esperan. */
export function esperasDeGrupos(grupos: readonly Pick<GrupoDTO, 'id' | 'esperaTemaId'>[], contratos: readonly ContratoDTO[]): ReadonlyMap<string, EsperaDeGrupo> {
  const porTema = new Map(contratos.map((c) => [c.temaId, c]));
  const fuera = new Map<string, EsperaDeGrupo>();
  for (const g of grupos) {
    const contrato = g.esperaTemaId === null ? undefined : porTema.get(g.esperaTemaId);
    if (contrato) fuera.set(g.id, { contrato, conSanti: contrato.lado !== YO });
  }
  return fuera;
}

/**
 * Por agente: «con Santi» si TODOS sus contratos abiertos tienen la pelota en el lado de Santi; y si hay tarjeta esperando
 * tu sí. Con grupos: el agente de un grupo cuyo contrato atado tiene la pelota en el lado de Santi también está «con
 * Santi» (`grupo` = ese grupo), salvo que un contrato SUYO tenga la pelota en el lado de Pau (el dueño sale en «Te toca»).
 */
export function santiPorAgente(
  contratos: readonly ContratoDTO[],
  grupos: readonly Pick<GrupoDTO, 'id' | 'esperaTemaId'>[] = [],
  contratados: readonly Pick<ContratadoDTO, 'agentId' | 'grupoId'>[] = [],
): SantiPorAgente {
  const porAgente = new Map<string, ContratoDTO[]>();
  for (const c of contratos) {
    if (!c.agentId) continue;
    porAgente.set(c.agentId, [...(porAgente.get(c.agentId) ?? []), c]);
  }
  const fuera = new Map<string, SantiDelAgente>();
  for (const [agentId, suyos] of porAgente) {
    fuera.set(agentId, { conSanti: suyos.every((c) => c.lado !== YO), esperaTuSi: suyos.some((c) => c.esperaTuSi) });
  }
  const esperas = esperasDeGrupos(grupos, contratos);
  for (const c of contratados) {
    const espera = c.grupoId ? esperas.get(c.grupoId) : undefined;
    if (!espera?.conSanti) continue;
    const propio = fuera.get(c.agentId);
    fuera.set(c.agentId, { conSanti: propio ? propio.conSanti : true, esperaTuSi: propio?.esperaTuSi ?? false, grupo: c.grupoId! });
  }
  return fuera;
}

/** Los grupos y quién está en cuál, con identidad estable mientras no cambie nada de eso (no cada aviso de un agente). */
function useGruposYMiembros(): { grupos: readonly GrupoDTO[]; miembros: readonly Pick<ContratadoDTO, 'agentId' | 'grupoId'>[] } {
  const grupos = useOficinaStore((s) => s.grupos);
  const firma = useOficinaStore((s) =>
    Object.values(s.contratados)
      .filter((c) => c.grupoId)
      .map((c) => `${c.agentId}:${c.grupoId}`)
      .sort()
      .join(','),
  );
  const miembros = useMemo(
    () => (firma ? firma.split(',').map((x) => ({ agentId: x.slice(0, x.lastIndexOf(':')), grupoId: x.slice(x.lastIndexOf(':') + 1) })) : []),
    [firma],
  );
  return { grupos, miembros };
}

/** El mapa vivo para la lista y la oficina (carga la lista de contratos si nadie la había pedido). */
export function useSantiPorAgente(): SantiPorAgente {
  const lista = usePelotasStore((s) => s.lista);
  const cargar = usePelotasStore((s) => s.cargarLista);
  const { grupos, miembros } = useGruposYMiembros();
  useEffect(() => {
    if (!usePelotasStore.getState().lista) void cargar();
  }, [cargar]);
  return useMemo(() => (lista?.configurado ? santiPorAgente(lista.contratos, grupos, miembros) : new Map()), [lista, grupos, miembros]);
}

/** Por grupo atado: su contrato y el lado de la pelota (el cartel del grupo y la cabecera de «Con Santi» en la lista). */
export function useEsperasDeGrupos(): ReadonlyMap<string, EsperaDeGrupo> {
  const lista = usePelotasStore((s) => s.lista);
  const grupos = useOficinaStore((s) => s.grupos);
  return useMemo(() => (lista?.configurado ? esperasDeGrupos(grupos, lista.contratos) : new Map()), [lista, grupos]);
}
